import type { ProductDetail } from "./types";
import { resolveCheckoutTruckOptions } from "@shared/products/truck-capacity";
import {
  canScheduleTruckloads,
  getCheckoutMinimumQuantity,
  getRecommendedTruckloadQuantity,
} from "@shared/products/checkout";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";

export interface ListingActionPrefill {
  quantity: number;
  deliveryDate: string;
  addressDisplay: string;
  address: {
    line1: string;
    city: string;
    state: string;
    zip: string;
  } | null;
}

export function getInitialListingQuantity(
  quantityValue: string | undefined,
  suggestedQuantity: number,
) {
  if (!quantityValue) return normalizeListingQuantity(suggestedQuantity);
  const parsedQuantity = parseInt(quantityValue, 10);
  if (Number.isNaN(parsedQuantity) || parsedQuantity < 1) {
    return normalizeListingQuantity(suggestedQuantity);
  }
  return normalizeListingQuantity(parsedQuantity);
}

export function getSuggestedListingQuantity(input: {
  checkoutMinimum: number;
  truckloadCapacity?: number;
}) {
  return normalizeListingQuantity(
    input.truckloadCapacity
      ? getRecommendedTruckloadQuantity(
          input.checkoutMinimum,
          input.truckloadCapacity,
        )
      : input.checkoutMinimum,
  );
}

export interface ListingQuantityPick {
  label: string;
  quantity: number;
}

export function getListingQuantityPicks(input: {
  checkoutMinimum: number;
  truckCapacity: number;
}): ListingQuantityPick[] {
  const picks: ListingQuantityPick[] = [];
  if (input.checkoutMinimum > 1) {
    picks.push({ label: "Minimum", quantity: input.checkoutMinimum });
  }
  const halfTruck = Math.round(input.truckCapacity / 2);
  if (input.truckCapacity >= 2 && halfTruck > input.checkoutMinimum) {
    picks.push({ label: "Half truckload", quantity: halfTruck });
  }
  if (input.truckCapacity > input.checkoutMinimum) {
    picks.push({ label: "Truckload", quantity: input.truckCapacity });
  }
  return picks;
}

export function normalizeListingQuantity(value: number) {
  if (!Number.isFinite(value)) return 1;
  return Math.max(1, Math.round(value));
}

export function getInitialQuoteQuantity(
  quantityValue: string | undefined,
  minOrderQuantity: number,
) {
  const parsedQuantity = quantityValue ? parseInt(quantityValue, 10) : NaN;
  return Number.isNaN(parsedQuantity) || parsedQuantity < 1
    ? minOrderQuantity
    : parsedQuantity;
}

export function getInitialQuoteDeliveryDate(
  deliveryDate: string | undefined,
  leadTimeDays: number,
): string {
  const requestedWindow = deliveryDate
    ? parseDeliveryWindowParam(`${deliveryDate}_${deliveryDate}`, leadTimeDays)
    : null;
  return requestedWindow?.start ?? defaultDeliveryWindow(leadTimeDays).start;
}
import {
  defaultDeliveryWindow,
  parseDeliveryWindowParam,
} from "@/lib/checkout/delivery-window";

export function getListingPurchasePolicy(
  data: Pick<
    ProductDetail,
    | "minOrderQuantity"
    | "minCheckoutQuantity"
    | "truckCapacities"
    | "productType"
  >,
) {
  const checkoutMinimum = getCheckoutMinimumQuantity(data);
  const truckOptions = resolveCheckoutTruckOptions(
    data.truckCapacities,
    data.productType?.defaultTruckloadQuantities,
  );
  const truckCapacity =
    (
      truckOptions.find((option) =>
        canScheduleTruckloads(
          checkoutMinimum,
          option.capacity,
          checkoutMinimum,
        ),
      ) ?? truckOptions[0]
    )?.capacity ?? 0;
  const suggestedQuantity = getSuggestedListingQuantity({
    checkoutMinimum,
    truckloadCapacity: truckCapacity,
  });
  return {
    checkoutMinimum,
    truckCapacity,
    truckOptions,
    canSchedule: canScheduleTruckloads(
      checkoutMinimum,
      truckOptions,
      suggestedQuantity,
    ),
    suggestedQuantity,
  };
}


export function getListingActionLabel(
  data: Pick<ProductDetail, "intent">,
  isCheckoutReady: boolean,
) {
  if (isCheckoutReady) return "View options";
  if (data.intent === "recycle") return "Respond to this listing";
  return CHECK_AVAILABILITY_LABEL;
}
